// @i18n-ignore-file

// Normalizes a dynamic value (label / title) into a stable, selector-safe testid
// suffix: lower-cased, non-alphanumerics collapsed to single hyphens, trimmed.
// e.g. "Women's New" -> "women-s-new"
const slugifyLocator = (value: string | number) =>
  String(value)
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')

export const TEST_LOCATORS = {
  // product form
  SLIDER_IMAGES_MAIN_IMAGE: 'slider-images-main-image',
  CART_POPUP_PRODUCT_FORM: 'cart-popup-product-form',
  CART_POPUP_BACKDROP: 'cart-popup-backdrop',
  CART_ICON_COUNT: 'cart-icon-count',
  PRODUCT_CTA: 'product-cta',
  PRODUCT_CTA_SECTION_END: 'product-cta-section-end',
  PRODUCT_FORM_SUBSCRIPTION_TERMS: 'product-form-subscription-terms',

  // product description
  PRODUCT_DETAILS_ICON_SECTION: 'product-details-icon-section',
  PRODUCT_DETAILS_ICON: 'product-details-icon',

  //bundle form
  BUNDLE_DISCOUNT: 'bundle-discount',
  BUNDLE_ADD_TO_CART: 'bundle-add-to-cart',
  BUNDLE_PRODUCT_LIST: 'bundle-product-list',
  BUNDLE_PRODUCT_SALE_PRICE: 'bundle-product-sale-price',

  BUNDLE_OPTIONAL_PRODUCTS: 'bundle-optional-products',
  BUNDLE_OPTIONAL_PRODUCTS_DETAILS: 'bundle-optional-products-details',

  //size chart
  SIZE_CHART_BUTTON: 'size-chart-button',
  SIZE_CHART: 'size-chart',

  // reviews
  FIRST_FOLD_REVIEW_COUNT: 'first-fold-review-count',
  REVIEW_SECTION_REVIEW_COUNT: 'review-section-review-count',
  REVIEW_SECTION_WRITE_REVIEW_BUTTON: 'review-section-write-review-button',
  REVIEW_STARS_WRAPPER: 'review-stars-wrapper',

  // wishlist
  WISHLIST_ICON_COUNT: 'wishlist-icon-count',
  ADD_TO_WISHLIST_BUTTON: 'add-to-wishlist-button',
  REMOVE_FROM_WISHLIST_BUTTON: 'remove-from-wishlist-button',
  DELETE_FROM_WISHLIST_BUTTON: 'delete-from-wishlist-button',

  // collection
  COLLECTION_FILTER: 'collection-filter',
  COLLECTION_PRODUCT_COUNT: 'collection-product-count',
  COLLECTION_BREADCRUMB: 'collection-breadcrumb',
  FILTER_SECTION_APPLIED_FILTERS: 'filter-section-applied-filters',
  FILTER_SECTION_CLEAR_ALL: 'filter-section-clear-all',
  COLLECTION_INFINITE_LOADER: 'collection-infinite-loader',
  COLLECTION_TITLE: 'collection-title',
  FILTER_DRAWER_PANEL: 'filter-drawer-panel',

  // product card
  PRODUCT_CARD: 'product-card',
  PRODUCT_CARD_TITLE: 'product-card-title',
  PRODUCT_CARD_SELECTED_COLOR: 'product-card-selected-color',
  PRODUCT_CARD_PRICE: 'product-card-price',

  // PDP recommendation sections
  PAIR_WITH_SECTION: 'pair_with_section',
  SIMILAR_PRODUCTS_SECTION: 'similar_products_section',
  YOU_MAY_ALSO_LIKE_SECTION: 'you_may_also_like_section',
  FREQUENTLY_BOUGHT_TOGETHER: 'frequently_bought_together_section',

  // Cart
  CART_DELETE_ITEM: 'cart-delete-item',
  CART_POPUP_CHECKOUT_BUTTON: 'cart-popup-checkout-button',
  CART_POPUP_CLOSE_BUTTON: 'cart-popup-close-button',

  // Header nav icons (rendered in both the desktop and mobile header rows)
  HEADER_CART_ICON: 'header-cart-icon',
  HEADER_SEARCH_ICON: 'header-search-icon',
  HEADER_WISHLIST_ICON: 'header-wishlist-icon',
  HEADER_ACCOUNT_ICON: 'header-account-icon',

  // Search
  NO_SEARCH_RESULTS: 'no-search-results',
  SEARCHBOX: 'searchbox',
  SEARCH_BUTTON: 'search-button',
  SEARCH_RESULTS_TITLE: 'search-results-title',

  // Global
  // Rich text
  RICH_TEXT_MOBILE: 'rich-text-mobile',
  RICH_TEXT_DESKTOP: 'rich-text-desktop',

  // Brand logo
  BRAND_LOGO: 'brand-logo',

  // Locale switcher
  LOCALISATION_CTA: 'localisation-cta',
  CHANGE_COUNTRY: 'change-country',
  LOCALE_SWITCHER_FORM: 'locale-switcher-form',

  // Mobile menu
  MOBILE_MENU_SUBMENU: 'mobile-menu-submenu',

  // App push banner
  APP_PUSH_BANNER: 'app-push-banner',

  // Navigation + Header
  NAV: {
    NAV_MAIN: 'nav-main',
    NAV_MAIN_SPACER: 'nav-main-spacer',
    NAV_MAIN_DESKTOP: 'nav-main-desktop',
    NAV_MAIN_MOBILE: 'nav-main-mobile',
    NAV_CATEGORY_ROW: 'nav-category-row',
    NAV_CATEGORY_ROW_REVEAL: 'nav-category-row-reveal',
    NAV_SEARCH: 'nav-search',
    NAV_BANNER: 'nav-banner',
    NAV_SEARCH_BACKDROP: 'nav-search-backdrop',
    NAV_SEARCH_OVERLAY_MOBILE: 'nav-search-overlay-mobile',
    NAV_SEARCH_CANCEL: 'nav-search-cancel',
    NAV_SEARCH_RESULT: 'nav-search-result',
    NAV_SEARCH_RESULT_GROUP: 'nav-search-result-group',
    // Dynamic locator for a top-level nav menu item, keyed off its label so a
    // specific menu entry can be targeted regardless of position (desktop + mobile).
    // e.g. NAV_MENU_ITEM('New Arrivals') -> 'nav-menu-item-new-arrivals'
    NAV_MENU_ITEM: (value: string) => `nav-menu-item-${slugifyLocator(value)}`,
  },

  // Category showcase section (DS). Desktop + mobile carousels are both mounted
  // simultaneously and toggled via CSS, so tile locators are namespaced per
  // platform to stay unique. Each locator targets a single category chip,
  // keyed off that tile's title.
  CATEGORY_SHOWCASE: {
    CAROUSEL_DESKTOP: 'category-showcase-carousel-desktop',
    CAROUSEL_MOBILE: 'category-showcase-carousel-mobile',
    // e.g. CAROUSEL_DESKTOP_TILE('Bedding') -> 'category-showcase-carousel-desktop-tile-bedding'
    CAROUSEL_DESKTOP_TILE: (value: string | number) =>
      `category-showcase-carousel-desktop-tile-${slugifyLocator(value)}`,
    CAROUSEL_MOBILE_TILE: (value: string | number) => `category-showcase-carousel-mobile-tile-${slugifyLocator(value)}`,
  },

  // Back-to-school recommendation widget (DS). Header shop button + one locator
  // per rendered product card, numbered 1-based and zero-padded to 2 digits.
  BACK_TO_SCHOOL: {
    // Keyed off the title text. Falls back to the bare locator when the title is empty.
    // e.g. TITLE('Back to School') -> 'recommendation-title-back-to-school'
    TITLE: (value?: string) => (value ? `recommendation-title-${slugifyLocator(value)}` : 'recommendation-title'),
    HEADER_BUTTON: 'recommendation-header-button',
    // e.g. PRODUCT(1) -> 'recommendation-product-01'
    PRODUCT: (position: number) => `recommendation-product-${String(position).padStart(2, '0')}`,
  },

  // Honest reviews section (DS). Header keyed off the heading text; one locator
  // per rendered review card, indexed 0-based.
  HONEST_REVIEWS: {
    // e.g. HEADER('Honest Reviews') -> 'review-honest-reviews'
    HEADER: (value?: string) => (value ? `review-${slugifyLocator(value)}` : 'review-header'),
    // e.g. PRODUCT(0) -> 'review-product-0'
    PRODUCT: (index: number) => `review-product-${index}`,
  },
}
